import { useRef, useEffect, useState } from 'react';
import { projects } from '../../../mocks/portfolio';

const ProjectsSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" ref={sectionRef} className="section-padding bg-stone-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-medium">Selected Works</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2 text-gray-900 dark:text-white">
              项目
              <span className="font-serif italic text-gradient ml-2">案例</span>
            </h2>
          </div>
          <p className="text-sm text-gray-400">每一个产品背后都是一段深度思考的旅程</p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((project, i) => (
            <article
              key={project.id}
              data-animate
              className="opacity-0 translate-y-8 transition-all duration-700"
              style={{ transitionDelay: `${i * 100}ms` }}
              onMouseEnter={() => setHovered(project.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className={`group bg-white dark:bg-gray-900 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-300 ${hovered === project.id ? '-translate-y-2 shadow-2xl shadow-gray-200/60 dark:shadow-black/40' : 'shadow-sm'}`}>
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-10`} />
                  <img
                    src={project.image}
                    alt={`${project.title} - AI 产品案例`}
                    title={project.titleEn}
                    className={`w-full h-full object-cover object-top transition-transform duration-500 ${hovered === project.id ? 'scale-105' : 'scale-100'}`}
                  />
                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/90 dark:bg-gray-900/90 text-gray-700 dark:text-gray-300 backdrop-blur-sm">
                      {project.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-black/20 text-white backdrop-blur-sm">
                      {project.year}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{project.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">{project.description}</p>

                  {/* Metrics */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.metrics.map((metric) => (
                      <span
                        key={metric}
                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400"
                      >
                        {metric}
                      </span>
                    ))}
                  </div>

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className={`flex items-center gap-2 text-sm font-medium text-orange-500 transition-all duration-200 ${hovered === project.id ? 'gap-3' : ''}`}>
                    <span>查看详情</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-500 flex justify-center mt-12">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center gap-2 px-8 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-orange-500 hover:text-orange-500 font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-message-line" />
            对这些项目感兴趣？欢迎联系我
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
